import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading.tsx";
import EditorToolbar, {
  formats,
  modules,
} from "@via/components/rich-text-editor/rte-editor-toolbar.tsx";
import { EditorState, MaximumBlobSize } from "@via/utils/constants.tsx";
import { getObjectSizeInBytes } from "@via/utils/helpers.tsx";
import { createMarkup } from "@via/utils/html-utils.tsx";
import { type JSX, useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./rte-editor.css";

type updateEditingStateType = (
  newEditState: EditorState,
  editorData?: string
) => void;

export default function Editor({
  updateEditingState,
  editingData,
  loading,
}: {
  updateEditingState: updateEditingStateType;
  editingData: string;
  loading?: boolean;
}): JSX.Element {
  const quillRef = useRef<ReactQuill>(null);

  const [content, setContent] = useState("");
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const createMarkupMemoized = useMemo(
    () => createMarkup(editingData),
    [editingData]
  );

  useEffect(() => {
    if (quillRef.current) {
      const quill = quillRef.current.getEditor();
      quill.setContents(quill.clipboard.convert(createMarkupMemoized.__html));
    }
  }, [editingData]);

  const handleChange = (value: string): void => {
    setContent(value);

    if (getObjectSizeInBytes({ content }) > MaximumBlobSize) {
      setHasError(true);
      setErrorMessage("Content size is too large");
    } else {
      setHasError(false);
      setErrorMessage("");
    }
  };

  const handleCancelClick = (): void => {
    updateEditingState(EditorState.Canceled);
  };

  const handleSaveClick = (): void => {
    updateEditingState(EditorState.Saved, createMarkup(content).__html);
  };

  return (
    <div className="pt-4">
      <EditorToolbar />
      <ReactQuill
        ref={quillRef}
        value={content}
        onChange={handleChange}
        modules={modules}
        formats={formats}
        placeholder="Start typing..."
      />
      {hasError ? (
        <div className="flex items-center justify-end text-red-500">
          <ExclamationTriangleIcon className="mr-2 h-6 w-6" /> {errorMessage}
        </div>
      ) : null}

      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 duration-200 ease-in-out hover:bg-gray-50"
          onClick={() => {
            handleCancelClick();
          }}>
          Cancel
        </button>
        <button
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm duration-200 ease-in-out hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          onClick={() => {
            handleSaveClick();
          }}
          disabled={loading || hasError}>
          <Spinner loading={loading}>Save</Spinner>
        </button>
      </div>
    </div>
  );
}
